import type { ReactNode } from "react";
import { JOINT_VENTURE_LINE } from "@sga/shared";
import { BrandMark } from "./BrandMark.js";

const TAGLINE =
  "Scholarship, Teachers Empowerment and Loco Parentis — one platform for every programme, form and applicant, year over year.";

/**
 * The three pre-auth screens share their card and field styling from here rather than each
 * repeating it, which is how the sign-in and TOTP inputs came to differ by a few pixels.
 *
 * Deliberately carries no font-size: a caller composing `text-lg` onto a class string that already
 * says `text-base` gets whichever Tailwind emitted last, not whichever it wrote last. Each screen
 * states its own size.
 */
export const AUTH_CARD_CLASS =
  "w-full space-y-5 rounded-xl border bg-card p-6 shadow-lg sm:p-8 lg:shadow-sm";
export const AUTH_INPUT_CLASS =
  "h-11 w-full rounded-md border bg-background px-3 focus:outline-none focus:ring-2 focus:ring-ring";

/**
 * Shared chrome for the three pre-auth screens (login, TOTP challenge, TOTP enrolment) — so a
 * coordinator flipping between them doesn't get a jarring layout change mid-flow. The illustration
 * is decorative only (`alt=""`); every screen still states its own purpose in its own heading for
 * anyone using a screen reader.
 *
 * Two layouts, not one layout with a hidden half. Below `lg` the illustrated panel used to simply
 * disappear, leaving a bare grey page with a white box on it — no logo, no colour, nothing that
 * said whose system this was, on the screen most staff actually sign in from. The phone gets its
 * own brand band instead: the same lockup and the same green, compressed, with the card lifted over
 * its lower edge.
 */
export const AuthShell = ({ children }: { children: ReactNode }): JSX.Element => (
  <main className="flex min-h-screen flex-col lg:flex-row">
    <div className="hidden flex-1 flex-col justify-between bg-gradient-to-br from-primary-hover to-primary p-10 text-primary-foreground lg:flex">
      <div>
        <BrandMark className="h-9" variant="on-primary" />
        <p className="mt-4 font-heading text-2xl font-semibold">Seplat Growth Academy</p>
        <p className="mt-1 max-w-sm text-sm text-primary-foreground/80">{TAGLINE}</p>
      </div>
      <img
        src="/illustrations/auth-registration.webp"
        alt=""
        className="max-h-[26rem] w-auto self-center object-contain"
      />
      <p className="text-xs text-primary-foreground/60">{JOINT_VENTURE_LINE}</p>
    </div>

    <div className="bg-gradient-to-br from-primary-hover to-primary px-6 pb-16 pt-10 text-primary-foreground lg:hidden">
      <BrandMark className="h-8" variant="on-primary" />
      <p className="mt-5 font-heading text-xl font-semibold">Seplat Growth Academy</p>
      <p className="mt-1 text-sm text-primary-foreground/80">
        Staff console for Scholarship, Teachers Empowerment and Loco Parentis.
      </p>
    </div>

    <div className="flex flex-1 flex-col items-center justify-center bg-muted px-4 pb-8 lg:p-4">
      {/* The negative margin only exists to overlap the mobile band above; on desktop there is no
          band, so it is cancelled rather than left to pull the card off-centre. */}
      <div className="-mt-10 w-full max-w-sm lg:mt-0">{children}</div>
      <p className="mt-8 max-w-xs text-center text-xs text-muted-foreground lg:hidden">
        {JOINT_VENTURE_LINE}
      </p>
    </div>
  </main>
);
